// src/components/hospitalizacion/BalanceHidricoCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import { WaterDrop, Visibility, KeyboardArrowLeft, KeyboardArrowRight, HighlightOff,ErrorOutline } from '@mui/icons-material';
import api from '../../api/api';
import dayjs from 'dayjs';

const ITEMS_PER_PAGE = 5;

const BalanceHidricoCard = ({ hospitalizacionId }) => {
  const [fluidos, setFluidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedFluido, setSelectedFluido] = useState(null);

  useEffect(() => {
    if (hospitalizacionId) {
      fetchBalance();
    }
  }, [hospitalizacionId]);

  const fetchBalance = async () => {
    try {
      setLoading(true);
      // El endpoint ahora trae tanto activos (planes) como terminados (registros de balance)
      const res = await api.get(`/hospitalizacion-fluidos/activos/${hospitalizacionId}`);
      setFluidos(res.data);
      setPage(1);
    } catch (err) {
      console.error('Error cargando balance hídrico:', err);
      setFluidos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = async (id_fluido) => {
  const volumenPasado = prompt("Ingrese el volumen total (ml) que realmente pasó al paciente:");
  
  if (volumenPasado !== null) { // Si no canceló el prompt
    try {
      await api.put(`/hospitalizacion-fluidos/${id_fluido}/finalizar`, null, {
        params: { 
          observaciones: `Finalizado manualmente. Vol total: ${volumenPasado}ml`,
          // Necesitarás agregar 'volumen_real' a tu endpoint PUT en el backend
          volumen_real: parseFloat(volumenPasado) 
        }
      });
      fetchBalance();
    } catch (err) {
      console.error(err);
    }
  }
};

  // Paginación
  const totalPages = Math.ceil(fluidos.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const fluidosOrdenados = [...fluidos].sort((a, b) => dayjs(b.fecha_inicio).unix() - dayjs(a.fecha_inicio).unix());
  const paginatedFluidos = fluidosOrdenados.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // --- NUEVA LÓGICA DE CÁLCULO CENTRALIZADA ---
  const totalIngresos = fluidos
    .filter(f => f.categoria === 'ingreso' || (f.categoria === 'plan_infusion' && f.volumen_real > 0))
    .reduce((acc, f) => acc + (Number(f.volumen_real) || 0), 0);

  // Egresos se mantienen igual
  const totalEgresos = fluidos
    .filter(f => f.categoria === 'egreso')
    .reduce((acc, f) => acc + (Number(f.volumen_real) || 0), 0);

  const balanceNeto = totalIngresos - totalEgresos;

  const handleVerDetalles = (fluido) => {
    setSelectedFluido(fluido);
  };

  const handleCloseDetalles = () => {
    setSelectedFluido(null);
  };

  const handlePageChange = (direction) => {
    if (direction === 'prev' && page > 1) setPage(page - 1);
    if (direction === 'next' && page < totalPages) setPage(page + 1);
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 2, bgcolor: '#0f172a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WaterDrop sx={{ mr: 1, color: '#38bdf8' }} />
            <Typography variant="subtitle1" fontWeight="bold">
              BALANCE HÍDRICO (IN/OUT)
            </Typography>
          </Box>

          <Chip
            label={`Neto: ${balanceNeto} ml`}
            color={balanceNeto >= 0 ? 'success' : 'error'}
            sx={{ fontWeight: 'bold', color: 'white' }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell>HORA</TableCell>
                <TableCell align="right">INGRESOS</TableCell>
                <TableCell>TIPO / VÍA</TableCell>
                <TableCell align="right">EGRESOS</TableCell>
                <TableCell>ORIGEN</TableCell>
                <TableCell>OBS</TableCell>
                <TableCell align="right">Detalles</TableCell>
              </TableRow>
            </TableHead>
           <TableBody>
  {paginatedFluidos.map((f) => (
    <TableRow 
      key={f.id_fluido} 
      hover 
      sx={{ bgcolor: f.categoria === 'plan_infusion' && f.estado === 'activo' ? '#f0f9ff' : 'inherit' }}
    >
      {/* Columna HORA: Fecha pequeña debajo de la hora */}
      <TableCell sx={{ fontWeight: 'bold' }}>
        <Typography variant="body2" fontWeight="bold">
          {dayjs(f.fecha_inicio).format('DD/MM/YYYY hh:mm A')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dayjs(f.fecha_inicio).format('DD/MM')}
        </Typography>
      </TableCell>

      {/* Columna INGRESOS: Suma ingresos directos Y planes finalizados */}
      {/* Columna Ingresos SUSTITUIDA */}
<TableCell align="right" sx={{ fontWeight: 600 }}>
  {f.categoria === 'ingreso' || (f.categoria === 'plan_infusion' && f.volumen_real > 0) ? (
    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
      +{f.volumen_real} ml
    </Typography>
  ) : f.categoria === 'plan_infusion' && f.estado === 'terminado' && (!f.volumen_real || f.volumen_real === 0) ? (
    /* Referencia visual de error cuando falta el dato */
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
      <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
        PENDIENTE
      </Typography>
      <Tooltip title="El plan terminó pero no se registró el volumen real. No suma al neto.">
        <ErrorOutline sx={{ color: '#f59e0b', fontSize: 16 }} />
      </Tooltip>
    </Stack>
  ) : (
    '—'
  )}
</TableCell>

      {/* Columna TIPO / VÍA */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{f.tipo_fluido}</Typography>
          {f.categoria === 'plan_infusion' && f.estado === 'activo' && (
            <Chip label="PLAN" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#0ea5e9', color: 'white' }} />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {f.via_origen && f.categoria !== 'egreso' ? f.via_origen : ''}
        </Typography>
      </TableCell>

      {/* Columna EGRESOS */}
      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
        {f.categoria === 'egreso' ? `-${f.volumen_real} ml` : ''}
      </TableCell>

      {/* Columna ORIGEN (Egresos) */}
      <TableCell>
        <Typography variant="body2">
          {f.categoria === 'egreso' ? (f.via_origen || 'Espontáneo') : ''}
        </Typography>
      </TableCell>

      {/* Columna OBS: Pequeño indicador de estado para planes */}
      <TableCell sx={{ fontSize: '0.75rem', maxWidth: 150 }}>
        {f.categoria === 'plan_infusion' && (
          <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
            {f.estado === 'activo' ? '🟠 En curso' : '🔵 Finalizado'}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap>
          {f.observaciones || '—'}
        </Typography>
      </TableCell>

      {/* Columna DETALLES / ACCIONES */}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {f.categoria === 'plan_infusion' && f.estado === 'activo' && (
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => handleFinalizar(f.id_fluido)}
              title="Finalizar Infusión"
            >
              <HighlightOff fontSize="small" /> 
            </IconButton>
          )}
  
          <IconButton size="small" onClick={() => handleVerDetalles(f)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  ))}
</TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton disabled={page === 1} onClick={() => handlePageChange('prev')}>
                <KeyboardArrowLeft />
              </IconButton>
              <Typography variant="body2">
                Página {page} de {totalPages}
              </Typography>
              <IconButton disabled={page === totalPages} onClick={() => handlePageChange('next')}>
                <KeyboardArrowRight />
              </IconButton>
            </Stack>
          </Box>
        )}
      </Card>

      {/* Modal de Detalles */}
      <Dialog open={!!selectedFluido} onClose={handleCloseDetalles} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f172a', color: 'white' }}>
          Detalles de Registro - {selectedFluido && dayjs(selectedFluido.fecha_inicio).format('DD/MM/YYYY hh:mm A')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Categoría</Typography>
              <Chip 
                label={selectedFluido?.categoria?.toUpperCase()} 
                size="small"
                color={selectedFluido?.categoria === 'ingreso' ? 'success' : selectedFluido?.categoria === 'egreso' ? 'error' : 'info'}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Fluido / Sustancia</Typography>
              <Typography fontWeight="medium">{selectedFluido?.tipo_fluido || '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Volumen Real</Typography>
              <Typography fontWeight="bold" color={selectedFluido?.categoria === 'egreso' ? 'error.main' : 'success.main'}>
                {selectedFluido?.volumen_real ? `${selectedFluido.volumen_real} ml` : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Vía / Origen</Typography>
              <Typography>{selectedFluido?.via_origen || 'No especificado'}</Typography>
            </Grid>
            
            {selectedFluido?.categoria === 'plan_infusion' && (
              <>
                <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Ritmo de Infusión</Typography>
                  <Typography>{selectedFluido?.ritmo_ml_h} ml/h</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Meta Total</Typography>
                  <Typography>{selectedFluido?.volumen_total_objetivo} ml</Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Observaciones Técnicas</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1, bgcolor: '#f8fafc', p: 2, borderRadius: 1 }}>
                  {selectedFluido?.observaciones || 'Sin observaciones registradas'}
                </Typography>
              </Box>
            </Grid>


            {/* INSERTAR después del Divider de observaciones en el modal */}
{selectedFluido?.categoria === 'plan_infusion' && 
 selectedFluido?.estado === 'terminado' && 
 (!selectedFluido?.volumen_real || selectedFluido?.volumen_real === 0) && (
  <Grid item xs={12}>
    <Box sx={{ 
      mt: 2, p: 2, 
      bgcolor: '#fff4e5', 
      border: '1px solid #ffe2b7', 
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1
    }}>
      <Typography variant="body2" color="#b45309" fontWeight="bold" textAlign="center">
        ⚠️ Este registro no está afectando al BALANCE NETO porque falta el volumen real.
      </Typography>
      <Button 
        size="small" 
        variant="contained" 
        color="warning"
        startIcon={<HighlightOff />}
        onClick={() => {
          handleFinalizar(selectedFluido.id_fluido);
          handleCloseDetalles();
        }}
      >
        Registrar Volumen Administrado
      </Button>
    </Box>
  </Grid>
)}
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Registrado por</Typography>
              <Typography fontWeight="medium" sx={{ color: 'primary.main' }}>
                {/* Ahora mostrará el nombre real del veterinario/técnico */}
                {selectedFluido?.usuario_nombre || 'Nombre no cargado'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID de Usuario: {selectedFluido?.created_by}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetalles} variant="contained" sx={{ bgcolor: '#0f172a' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BalanceHidricoCard;